import { ChangeEvent, HTMLInputTypeAttribute } from "react";
import { Controller } from "react-hook-form";
import * as MuiIcon from "@mui/icons-material";
import { Path } from "react-hook-form/dist/types/path";
import { Control } from "react-hook-form/dist/types/form";
import { FieldValues } from "react-hook-form/dist/types/fields";
import { ControllerRenderProps } from "react-hook-form/dist/types";
import { Box, FormLabel, InputAdornment, Typography } from "@mui/material";
import FormField, { FormFieldProps } from "./FormField";

export interface IFormTextField<TFieldValues extends FieldValues = FieldValues>
  extends Omit<FormFieldProps, "onChange" | "label"> {
  label?: string | React.ReactElement;
  tooltip?: string | undefined;
  gutters?: boolean;
  fieldName?: Path<TFieldValues> | undefined;
  fieldControl?: Control<TFieldValues, any> | undefined;
  fieldType?: HTMLInputTypeAttribute | undefined;
  onChange?: (
    ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    field?: ControllerRenderProps<TFieldValues, Path<TFieldValues>> | undefined
  ) => any | undefined;
}

export default function FormTextField<TFieldValues extends FieldValues = FieldValues>({
  size,
  label,
  tooltip,
  onChange,
  fieldType,
  fieldName,
  fieldControl,
  gutters = true,
  ...props
}: IFormTextField<TFieldValues>) {
  return (
    <Box className={gutters ? "mb-2" : ""}>
      {label && (
        <FormLabel htmlFor={props.id}>
          <Typography variant="body2">
            {label}
            {tooltip && (
              <MuiIcon.InfoOutlined sx={{ width: 20, height: 20 }} className="ml-2 -mt-1" />
            )}
          </Typography>
        </FormLabel>
      )}
      {fieldControl && fieldName ? (
        <Controller
          control={fieldControl}
          name={fieldName}
          render={({ field, fieldState }) => {
            return (
              <FormField
                type={fieldType}
                onChange={ev => {
                  if (!props.disabled) {
                    onChange?.(ev, field);
                    field.onChange(ev);
                  }
                }}
                size={size}
                onBlur={field.onBlur}
                name={field.name}
                value={field.value}
                ref={field.ref}
                error={fieldState.invalid}
                helperText={fieldState.error?.message ?? " "}
                InputProps={{
                  id: props.id,
                  endAdornment: (
                    <InputAdornment position="end">
                      <MuiIcon.Check
                        color={
                          fieldState.invalid || !fieldState.isTouched ? "disabled" : "primary"
                        }
                      />
                    </InputAdornment>
                  )
                }}
                {...props}
              />
            );
          }}
        />
      ) : (
        <FormField type={fieldType} {...props} />
      )}
    </Box>
  );
}
